import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { AtencionBanner, EstadoBadge, SemaforoResumen, ORDEN_ESTADO, SerieTemporal } from "../analisis/comun";

// ═══════════════════════════════════════════════════════════════════════════
// UTILS
// ═══════════════════════════════════════════════════════════════════════════
const todayISO = () => new Date().toISOString().slice(0, 10);
const lunesISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
  return d.toISOString().slice(0, 10);
};
const addDays = (iso, n) => {
  const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10);
};
const diffDays = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

const PERIODOS = [
  { key: "semana",  label: "Esta semana" },
  { key: "sem_ant", label: "Sem. ant." },
  { key: "mes",     label: "Este mes" },
  { key: "mes_ant", label: "Mes ant." },
  { key: "3meses",  label: "3 meses" },
  { key: "custom",  label: "Personalizado" },
];

function calcPeriodo(key) {
  const d = new Date(), hoy = todayISO(), lun = lunesISO();
  if (key === "semana")  return { desde: lun, hasta: hoy };
  if (key === "sem_ant") return { desde: addDays(lun, -7), hasta: addDays(lun, -1) };
  if (key === "mes") {
    return { desde: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`, hasta: hoy };
  }
  if (key === "mes_ant") {
    const pm = new Date(d.getFullYear(), d.getMonth()-1, 1);
    const um = new Date(d.getFullYear(), d.getMonth(), 0);
    return { desde: pm.toISOString().slice(0,10), hasta: um.toISOString().slice(0,10) };
  }
  if (key === "3meses") {
    const ini = new Date(d.getFullYear(), d.getMonth()-2, 1);
    return { desde: ini.toISOString().slice(0,10), hasta: hoy };
  }
  return { desde: lun, hasta: hoy };
}

function prevPeriodo({ desde, hasta }) {
  const n = diffDays(desde, hasta) + 1;
  const prevHasta = addDays(desde, -1);
  const prevDesde = addDays(prevHasta, -(n - 1));
  return { desde: prevDesde, hasta: prevHasta };
}

// ═══════════════════════════════════════════════════════════════════════════
// LÓGICA SEMÁFOROS
// ═══════════════════════════════════════════════════════════════════════════
const C  = { verde: "#16a34a", amarillo: "#d97706", rojo: "#dc2626", gris: "#94a3b8" };
const BG = { verde: "#dcfce7", amarillo: "#fef3c7", rojo: "#fee2e2", gris: "#f1f5f9" };

function pctTareas(tareas) {
  if (!tareas) return null;
  let plan = 0, si = 0;
  Object.values(tareas).forEach(v => { plan += v.planificadas; si += v.si; });
  return plan === 0 ? null : Math.round((si / plan) * 100);
}
function semaforo(pct) {
  if (pct === null || pct === undefined) return "gris";
  return pct >= 80 ? "verde" : pct >= 60 ? "amarillo" : "rojo";
}
function pctReporte(rep) {
  if (!rep || rep.length === 0) return null;
  let s = 0, m = 0;
  rep.forEach(q => {
    s += q.stats.verde * 2 + q.stats.amarillo;
    m += (q.stats.verde + q.stats.amarillo + q.stats.rojo) * 2;
  });
  return m === 0 ? null : Math.round((s / m) * 100);
}

// Estado de la asistenta según seguimiento + evaluación (🔴/🟡/🟢/⬜)
function estadoAsistenta(a) {
  const pct = pctTareas(a.tareas);
  const pctR = pctReporte(a.reporte_asistenta);
  if (pct == null && pctR == null) return 'sin_reportes';
  const inc = a.incidencias.reduce((s, i) => s + i.count, 0);
  const stT = semaforo(pct), stR = semaforo(pctR);
  if (stT === 'rojo' || stR === 'rojo' || a.valoracion.mal >= 3 || inc >= 3 || (a.n_no_validados || 0) > 0) return 'critico';
  if (stT === 'amarillo' || stR === 'amarillo' || inc > 0 || (a.n_pendientes_validacion || 0) > 0) return 'atencion';
  return 'bien';
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTES ATOM
// ═══════════════════════════════════════════════════════════════════════════
const MiniDonut = ({ pct, size = 52 }) => {
  const col = C[semaforo(pct)];
  const r = (size - 10) / 2, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = pct != null ? Math.max(0, Math.min(1, pct / 100)) * circ : 0;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={5} />
      {dash > 0 && (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth={5}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: "stroke-dasharray 0.5s" }} />
      )}
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
        fontSize={pct != null ? 11 : 9} fontWeight={700} fill={col}>
        {pct != null ? `${pct}%` : "—"}
      </text>
    </svg>
  );
};

const Tendencia = ({ delta }) => {
  if (delta === null || delta === undefined) return null;
  if (Math.abs(delta) <= 3) return <span style={{ color: C.gris, fontSize: 12, fontWeight: 700 }}>→ estable</span>;
  if (delta > 0) return <span style={{ color: C.verde, fontSize: 12, fontWeight: 700 }}>↑ +{delta}%</span>;
  return <span style={{ color: C.rojo, fontSize: 12, fontWeight: 700 }}>↓ {delta}%</span>;
};

const SemaforoBadge = ({ tipo, label }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    background: BG[tipo] || BG.gris, color: C[tipo] || C.gris,
    border: `1.5px solid ${C[tipo] || C.gris}`,
    borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700,
  }}>
    {tipo === "verde" ? "🟢" : tipo === "amarillo" ? "🟡" : tipo === "rojo" ? "🔴" : "⬜"}
    {" "}{label}
  </span>
);

const ProgresoBarra = ({ pct, color, height = 8 }) => (
  <div style={{ background: "#e2e8f0", borderRadius: 99, height, overflow: "hidden", flex: 1 }}>
    <div style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.5s" }} />
  </div>
);

const TAREAS_INFO = {
  limpieza_hab: { label: "Limpieza hab.", emoji: "🛏️" },
  lavado_ropa:  { label: "Lavado ropa",   emoji: "👕" },
  planchado:    { label: "Planchado",     emoji: "👔" },
  banos:        { label: "Baños",         emoji: "🚿" },
  salon:        { label: "Salón",         emoji: "🛋️" },
  cocina:       { label: "Cocina",        emoji: "🍳" },
};

// ═══════════════════════════════════════════════════════════════════════════
// TABS DEL DETALLE
// ═══════════════════════════════════════════════════════════════════════════
const MetricCard = ({ label, value, color, bg, sub }) => (
  <div style={{ background: bg, borderRadius: 12, padding: "12px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, textAlign: "center" }}>
    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
    <div style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</div>
  </div>
);

const CompararFila = ({ label, curr, prev, invert, unidad = "" }) => {
  const delta = curr != null && prev != null ? curr - prev : null;
  const mejora = invert ? delta < 0 : delta > 0;
  const col = delta === null || delta === 0 ? C.gris : mejora ? C.verde : C.rojo;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid #f1f5f9" }}>
      <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 12, color: "#94a3b8" }}>ant: {prev != null ? `${prev}${unidad}` : "—"}</span>
      <span style={{ fontSize: 14, fontWeight: 700 }}>{curr != null ? `${curr}${unidad}` : "—"}</span>
      {delta !== null && delta !== 0 && (
        <span style={{ fontSize: 12, fontWeight: 700, color: col, minWidth: 40 }}>
          {mejora ? "↑" : "↓"} {Math.abs(delta)}{unidad}
        </span>
      )}
    </div>
  );
};

const TabResumen = ({ a, prev, delta }) => {
  const pct  = pctTareas(a.tareas);
  const pctR = pctReporte(a.reporte_asistenta);
  const pieData = [
    { name: "Bien", value: a.valoracion.bien, fill: C.verde },
    { name: "Mal",  value: a.valoracion.mal,  fill: C.rojo  },
  ].filter(x => x.value > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
        <MetricCard label="Tareas" value={pct != null ? `${pct}%` : "—"} color={C[semaforo(pct)]} bg={BG[semaforo(pct)]} sub={<Tendencia delta={delta?.tareas} />} />
        <MetricCard label="Evaluación" value={pctR != null ? `${pctR}%` : "—"} color={C[semaforo(pctR)]} bg={BG[semaforo(pctR)]} sub="supervisora" />
        <MetricCard label="Jornadas" value={a.n_cerrados} color="#3b82f6" bg="#eff6ff" sub={`de ${a.n_planificados} plan.`} />
        <MetricCard label="Validados" value={a.n_validados || 0} color="#16a34a" bg="#f0fdf4"
          sub={`${a.n_cerrados > 0 ? Math.round(((a.n_validados||0)/a.n_cerrados)*100) : 0}% de días`} />
      </div>

      {pieData.length > 0 && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>😊 Valoración de jornadas</h4>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <PieChart width={100} height={100}>
              <Pie data={pieData} cx={45} cy={45} innerRadius={24} outerRadius={44} dataKey="value" paddingAngle={3}>
                {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v} días`, n]} />
            </PieChart>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: C.verde }} />
                <span style={{ fontSize: 14, fontWeight: 700 }}>{a.valoracion.bien} días bien</span>
                {prev && <span style={{ fontSize: 11, color: C.gris }}>(ant: {prev.valoracion?.bien ?? "—"})</span>}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: C.rojo }} />
                <span style={{ fontSize: 14, fontWeight: 700 }}>{a.valoracion.mal} días mal</span>
                {prev && <span style={{ fontSize: 11, color: C.gris }}>(ant: {prev.valoracion?.mal ?? "—"})</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {prev && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>📊 Comparación con período anterior</h4>
          <CompararFila label="% Tareas completadas" curr={pct} prev={pctTareas(prev.tareas)} unidad="%" />
          <CompararFila label="Evaluación supervisora" curr={pctR} prev={pctReporte(prev.reporte_asistenta)} unidad="%" />
          <CompararFila label="Días con valoración mal" curr={a.valoracion.mal} prev={prev.valoracion?.mal ?? 0} invert unidad="x" />
          <CompararFila label="Incidencias" curr={a.incidencias.reduce((s,i)=>s+i.count,0)} prev={(prev.incidencias||[]).reduce((s,i)=>s+i.count,0)} invert unidad="x" />
          <CompararFila label="Cambios de tarea" curr={Object.values(a.tareas).reduce((s,v)=>s+(v.cambio||0),0)} prev={Object.values(prev.tareas||{}).reduce((s,v)=>s+(v.cambio||0),0)} invert unidad="x" />
        </div>
      )}
    </div>
  );
};

const TabTareas = ({ a }) => {
  const barData = Object.entries(a.tareas)
    .filter(([, v]) => v.planificadas > 0)
    .map(([t, v]) => ({
      name: (TAREAS_INFO[t]?.emoji || "") + " " + (TAREAS_INFO[t]?.label || t),
      Hecho:   v.si,
      Cambio:  v.cambio || 0,
      NoHecho: v.no,
    }));

  const totalCambios = Object.values(a.tareas).reduce((s, v) => s + (v.cambio || 0), 0);
  const hayRazones = Object.values(a.razones).some(arr => arr.length > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={cs.sec}>
        <h4 style={cs.sh}>📊 Desglose por tarea</h4>
        <div style={{ display: "flex", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12 }}><div style={{ width: 10, height: 10, background: C.verde, borderRadius: 2 }} />Hecho</div>
          <div style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12 }}><div style={{ width: 10, height: 10, background: C.amarillo, borderRadius: 2 }} />Cambio</div>
          <div style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12 }}><div style={{ width: 10, height: 10, background: C.rojo, borderRadius: 2 }} />No hecho</div>
        </div>
        <ResponsiveContainer width="100%" height={Math.max(180, barData.length * 44 + 20)}>
          <BarChart data={barData} layout="vertical" margin={{ left: 4, right: 20, top: 0, bottom: 0 }}>
            <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, n) => [`${v} días`, n]} />
            <Bar dataKey="Hecho"   fill={C.verde}    stackId="a" />
            <Bar dataKey="Cambio"  fill={C.amarillo} stackId="a" />
            <Bar dataKey="NoHecho" fill={C.rojo}     stackId="a" radius={[0,4,4,0]} name="No hecho" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {totalCambios > 0 && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>🔄 Cambios / Sustituciones ({totalCambios})</h4>
          <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 10px" }}>
            La tarea se realizó de forma diferente a la planificada (ej: lavado a mano, ropa no secada, etc.)
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.entries(a.tareas)
              .filter(([, v]) => (v.cambio || 0) > 0)
              .map(([t, v]) => (
                <span key={t} style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", borderRadius: 8, padding: "5px 12px", fontSize: 13, fontWeight: 700 }}>
                  {TAREAS_INFO[t]?.emoji} {TAREAS_INFO[t]?.label} × {v.cambio}
                </span>
              ))}
          </div>
        </div>
      )}

      {hayRazones && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>❓ Razones de no realización</h4>
          {Object.entries(a.razones).map(([tarea, razones]) =>
            razones.length === 0 ? null : (
              <div key={tarea} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6, padding: "4px 8px", background: "#f1f5f9", borderRadius: 6, display: "inline-block" }}>
                  {TAREAS_INFO[tarea]?.emoji} {TAREAS_INFO[tarea]?.label}
                </div>
                {razones.map(r => {
                  const maxC = Math.max(...razones.map(x => x.count));
                  return (
                    <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 18, flexShrink: 0 }}>{r.emoji}</span>
                      <span style={{ flex: 1, fontSize: 13 }}>{r.titulo}</span>
                      <ProgresoBarra pct={(r.count / maxC) * 100} color={C.rojo} height={8} />
                      <span style={{ fontSize: 14, fontWeight: 800, color: C.rojo, minWidth: 28, textAlign: "right" }}>×{r.count}</span>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      )}

      {a.comentarios && a.comentarios.length > 0 && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>💬 Comentarios de cierre</h4>
          {a.comentarios.map((c, i) => (
            <div key={i} style={{ background: "#fef9c3", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#374151", marginBottom: 6, fontStyle: "italic", border: "1px solid #fde68a" }}>
              "{c}"
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TabIncidencias = ({ a }) => {
  const totalInc = a.incidencias.reduce((s, i) => s + i.count, 0);
  if (totalInc === 0 && a.incidencias_textos.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px", color: C.gris }}>
        <div style={{ fontSize: 48, marginBottom: 10 }}>✅</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#374151" }}>Sin incidencias</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>No se registró ninguna incidencia en este período</div>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {a.incidencias.length > 0 && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>⚠️ Incidencias registradas ({totalInc} total)</h4>
          {a.incidencias.map(inc => {
            const maxC = Math.max(...a.incidencias.map(i => i.count));
            return (
              <div key={inc.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>{inc.emoji}</span>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{inc.titulo}</span>
                <ProgresoBarra pct={(inc.count / maxC) * 100} color="#f97316" height={8} />
                <span style={{ fontSize: 16, fontWeight: 800, color: "#c2410c", minWidth: 30, textAlign: "right" }}>×{inc.count}</span>
              </div>
            );
          })}
        </div>
      )}
      {a.incidencias_textos.length > 0 && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>📝 Notas libres</h4>
          {a.incidencias_textos.map((t, i) => (
            <div key={i} style={{ background: "#fff7ed", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#7c2d12", marginBottom: 6, fontStyle: "italic", border: "1px solid #fed7aa" }}>
              "{t}"
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TabEvaluacion = ({ a }) => {
  const pct = pctReporte(a.reporte_asistenta);
  const hayDatos = a.reporte_asistenta.some(q => q.stats.total > 0);
  if (!hayDatos) {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px", color: C.gris }}>
        <div style={{ fontSize: 48, marginBottom: 10 }}>📋</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#374151" }}>Sin evaluaciones</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Nadie completó el reporte diario en este período</div>
      </div>
    );
  }

  const chartData = a.reporte_asistenta
    .filter(q => q.stats.total > 0)
    .map(q => ({ name: q.emoji + " " + q.subcategoria, Verde: q.stats.verde, Amarillo: q.stats.amarillo, Rojo: q.stats.rojo }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ ...cs.sec, display: "flex", alignItems: "center", gap: 16 }}>
        <MiniDonut pct={pct} size={68} />
        <div>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 4 }}>Puntuación global de evaluación</div>
          <SemaforoBadge tipo={semaforo(pct)} label={pct != null ? `${pct}%` : "—"} />
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
            {a.reporte_asistenta.reduce((s, q) => s + q.stats.total, 0)} evaluaciones recibidas
          </div>
        </div>
      </div>

      {chartData.length > 0 && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>📊 Gráfico por criterio</h4>
          <div style={{ display: "flex", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12 }}><div style={{ width: 10, height: 10, background: C.verde, borderRadius: 2 }} />Verde</div>
            <div style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12 }}><div style={{ width: 10, height: 10, background: C.amarillo, borderRadius: 2 }} />Amarillo</div>
            <div style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12 }}><div style={{ width: 10, height: 10, background: C.rojo, borderRadius: 2 }} />Rojo</div>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 46 + 20)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 4, right: 20, top: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={135} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n) => [`${v} veces`, n]} />
              <Bar dataKey="Verde"    fill={C.verde}    stackId="a" />
              <Bar dataKey="Amarillo" fill={C.amarillo} stackId="a" />
              <Bar dataKey="Rojo"     fill={C.rojo}     stackId="a" radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={cs.sec}>
        <h4 style={cs.sh}>📋 Detalle por criterio</h4>
        {a.reporte_asistenta.filter(q => q.stats.total > 0).map(q => {
          const t = q.stats.verde + q.stats.amarillo + q.stats.rojo;
          const pV = Math.round((q.stats.verde / t) * 100);
          const pA = Math.round((q.stats.amarillo / t) * 100);
          const pR = 100 - pV - pA;
          const col = semaforo(pV);
          return (
            <div key={q.id_pregunta} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 18 }}>{q.emoji}</span>
                <span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{q.subcategoria}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: C[col] }}>{pV}% 🟢</span>
              </div>
              <div style={{ display: "flex", height: 10, borderRadius: 99, overflow: "hidden" }}>
                {pV > 0 && <div style={{ width: `${pV}%`, background: C.verde }} />}
                {pA > 0 && <div style={{ width: `${pA}%`, background: C.amarillo }} />}
                {pR > 0 && <div style={{ width: `${pR}%`, background: C.rojo }} />}
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 3 }}>
                <span style={{ fontSize: 11, color: C.verde }}>🟢 {q.stats.verde}</span>
                <span style={{ fontSize: 11, color: C.amarillo }}>🟡 {q.stats.amarillo}</span>
                <span style={{ fontSize: 11, color: C.rojo }}>🔴 {q.stats.rojo}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MODAL DETALLE CENTRADO
// ═══════════════════════════════════════════════════════════════════════════
const TabValidaciones = ({ a }) => {
  const nV = a.n_validados || 0;
  const nNV = a.n_no_validados || 0;
  const nP = a.n_pendientes_validacion || 0;
  const total = nV + nNV + nP;
  const pctVal = total > 0 ? Math.round((nV / total) * 100) : null;

  const pieData = [
    { name: 'Validados', value: nV, fill: '#16a34a' },
    { name: 'No válidos', value: nNV, fill: '#dc2626' },
    { name: 'Pendientes', value: nP, fill: '#9ca3af' },
  ].filter(d => d.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '12px 8px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Validados</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#16a34a' }}>{nV}</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>{pctVal != null ? `${pctVal}%` : '—'}</div>
        </div>
        <div style={{ background: '#fff1f2', borderRadius: 12, padding: '12px 8px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>No válidos</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#dc2626' }}>{nNV}</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>{total > 0 ? `${Math.round((nNV/total)*100)}%` : '—'}</div>
        </div>
        <div style={{ background: '#f8fafc', borderRadius: 12, padding: '12px 8px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Sin validar</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#9ca3af' }}>{nP}</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>de {total}</div>
        </div>
      </div>

      {/* Pie */}
      {pieData.length > 0 && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>Estado de validaciones</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <PieChart width={100} height={100}>
              <Pie data={pieData} cx={45} cy={45} innerRadius={24} outerRadius={44} dataKey="value" paddingAngle={3}>
                {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip />
            </PieChart>
            <div style={{ flex: 1 }}>
              {pieData.map(d => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: d.fill }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: d.fill }}>{d.value}</span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{d.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Incidencias de validaciones de día */}
      {(a.incidencias_dia || []).length > 0 && (
        <div style={cs.sec}>
          <h4 style={cs.sh}>⚠️ Incidencias registradas en validaciones</h4>
          {(a.incidencias_dia || []).map(inc => (
            <div key={inc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 18 }}>{inc.emoji}</span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{inc.titulo}</span>
              <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>×{inc.count}</span>
            </div>
          ))}
        </div>
      )}

      {total === 0 && (
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: 20 }}>
          No hay validaciones de día en este período
        </div>
      )}
    </div>
  );
};

const TabEvolucion = ({ a }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <SerieTemporal
      data={a.serie_tareas || []}
      series={[
        { key: 'tareas', label: 'Tareas %', color: '#22c55e' },
        { key: 'eval', label: 'Evaluación %', color: '#3b82f6' },
      ]}
      titulo="Evolución semanal"
      height={200}
    />
  </div>
);

const TABS = [
  { key: "resumen",      label: "📈 Resumen" },
  { key: "tareas",       label: "✅ Tareas" },
  { key: "validaciones", label: "🔍 Validaciones" },
  { key: "incidencias",  label: "⚠️ Incidencias" },
  { key: "evaluacion",   label: "📋 Evaluación" },
  { key: "evolucion",    label: "📊 Evolución" },
];

const DetalleModal = ({ a, prev, delta, onClose }) => {
  const [tab, setTab] = useState("resumen");
  const st = semaforo(pctTareas(a.tareas));
  return (
    <div style={cs.overlay} onClick={onClose}>
      <div style={cs.modal} onClick={e => e.stopPropagation()}>
        <div style={{ ...cs.mHead, borderLeft: `5px solid ${C[st]}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {a.nombre}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {a.pisos?.join(" · ")}  ·  {a.supervisora}
            </div>
          </div>
          <button onClick={onClose} style={cs.btnX}>✕</button>
        </div>
        <div style={cs.tabBar}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ ...cs.tabBtn, ...(tab === t.key ? cs.tabActive : {}) }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={cs.mBody}>
          {tab === "resumen"      && <TabResumen      a={a} prev={prev} delta={delta} />}
          {tab === "tareas"       && <TabTareas       a={a} />}
          {tab === "validaciones" && <TabValidaciones a={a} />}
          {tab === "incidencias"  && <TabIncidencias  a={a} />}
          {tab === "evaluacion"   && <TabEvaluacion   a={a} />}
          {tab === "evolucion"    && <TabEvolucion     a={a} />}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// RESUMEN PANEL SUPERIOR
// ═══════════════════════════════════════════════════════════════════════════
const ResumenPanel = ({ asistentas }) => {
  const counts = { verde: 0, amarillo: 0, rojo: 0, alertas: 0 };
  asistentas.forEach(a => {
    const st = semaforo(pctTareas(a.tareas));
    counts[st] = (counts[st] || 0) + 1;
    const inc = a.incidencias.reduce((s, i) => s + i.count, 0);
    if (st === "rojo" || a.valoracion.mal >= 3 || inc >= 3) counts.alertas++;
  });
  const avgPct = asistentas.length === 0 ? null :
    Math.round(asistentas.reduce((s, a) => s + (pctTareas(a.tareas) ?? 0), 0) / asistentas.length);
  const pieData = [
    { name: "Verde",    value: counts.verde,    fill: C.verde },
    { name: "Amarillo", value: counts.amarillo, fill: C.amarillo },
    { name: "Rojo",     value: counts.rojo,     fill: C.rojo },
  ].filter(x => x.value > 0);

  return (
    <div style={{ background: "#fff", margin: "12px 16px 4px", borderRadius: 16, padding: "14px 16px", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
      {pieData.length > 0 && (
        <PieChart width={100} height={100}>
          <Pie data={pieData} cx={45} cy={45} innerRadius={26} outerRadius={45} dataKey="value" paddingAngle={3}>
            {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [`${v} asistentas`, n]} />
        </PieChart>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, flex: 1 }}>
        {[["verde","🟢","dcfce7","16a34a"],["amarillo","🟡","fef3c7","d97706"],["rojo","🔴","fee2e2","dc2626"]].map(([k,e,bg,col]) => (
          <div key={k} style={{ background: `#${bg}`, borderRadius: 10, padding: "8px 10px" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: `#${col}` }}>{counts[k]}</div>
            <div style={{ fontSize: 11, color: `#${col}` }}>{e} {k.charAt(0).toUpperCase() + k.slice(1)}</div>
          </div>
        ))}
        <div style={{ background: counts.alertas > 0 ? "#fef2f2" : "#f1f5f9", borderRadius: 10, padding: "8px 10px" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: counts.alertas > 0 ? "#b91c1c" : "#64748b" }}>{counts.alertas}</div>
          <div style={{ fontSize: 11, color: counts.alertas > 0 ? "#b91c1c" : "#64748b" }}>⚠️ Alertas</div>
        </div>
      </div>
      {avgPct != null && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <MiniDonut pct={avgPct} size={64} />
          <div style={{ fontSize: 11, color: "#64748b", textAlign: "center" }}>media<br/>global</div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TARJETA ASISTENTA
// ═══════════════════════════════════════════════════════════════════════════
const TarjetaAsistenta = ({ a, prev, delta, onClick }) => {
  const pct   = pctTareas(a.tareas);
  const pctR  = pctReporte(a.reporte_asistenta);
  const stT   = semaforo(pct);
  const stR   = semaforo(pctR);
  const totalInc = a.incidencias.reduce((s, i) => s + i.count, 0);
  const totalCambios = Object.values(a.tareas).reduce((s, v) => s + (v.cambio || 0), 0);
  const esAlerta = stT === "rojo" || stR === "rojo" || a.valoracion.mal >= 3 || totalInc >= 3;

  return (
    <div onClick={onClick} style={{
      background: "#fff", borderRadius: 14, padding: "12px 14px", marginBottom: 8,
      cursor: "pointer", borderLeft: `5px solid ${C[stT]}`,
      border: `1.5px solid ${esAlerta ? "#fca5a5" : "#e2e8f0"}`,
      borderLeftWidth: 5, borderLeftColor: C[stT],
      boxShadow: esAlerta ? "0 2px 8px rgba(220,38,38,0.1)" : "0 1px 4px rgba(0,0,0,0.05)",
      transition: "box-shadow 0.2s",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <MiniDonut pct={pct} size={50} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
            {esAlerta && <span style={{ fontSize: 14 }}>⚠️</span>}
            <span style={{ fontWeight: 800, fontSize: 15, color: "#1e293b" }}>{a.nombre}</span>
            <EstadoBadge estado={estadoAsistenta(a)} />
            <Tendencia delta={delta?.tareas} />
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>
            {a.pisos?.slice(0, 4).join(" · ")}{a.pisos?.length > 4 ? ` +${a.pisos.length - 4}` : ""}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <SemaforoBadge tipo={stT} label={`${pct != null ? pct + "%" : "—"}`} />
            {pctR != null && <SemaforoBadge tipo={stR} label={`📋 ${pctR}%`} />}
            {a.valoracion.mal > 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, color: a.valoracion.mal >= 3 ? C.rojo : C.amarillo, background: a.valoracion.mal >= 3 ? "#fee2e2" : "#fef3c7", borderRadius: 12, padding: "3px 8px" }}>
                😟 {a.valoracion.mal} mal
              </span>
            )}
            {totalInc > 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, color: totalInc >= 3 ? "#c2410c" : C.amarillo, background: totalInc >= 3 ? "#fff7ed" : "#fef3c7", borderRadius: 12, padding: "3px 8px" }}>
                ⚠️ {totalInc} incid.
              </span>
            )}
            {totalCambios > 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e", background: "#fef3c7", borderRadius: 12, padding: "3px 8px" }}>
                🔄 {totalCambios} cambios
              </span>
            )}
          </div>
        </div>
        <span style={{ color: "#94a3b8", fontSize: 18, flexShrink: 0 }}>▶</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
const AnalisisAsitentas = () => {
  const navigate = useNavigate();
  useAuth();

  const [periodoKey, setPeriodoKey] = useState("semana");
  const [custom,     setCustom    ] = useState({ desde: lunesISO(), hasta: todayISO() });
  const [datos,      setDatos     ] = useState(null);
  const [datosPrev,  setDatosPrev ] = useState(null);
  const [loading,    setLoading   ] = useState(true);
  const [error,      setError     ] = useState("");
  const [seleccionada, setSeleccionada] = useState(null);
  const [busqueda,   setBusqueda  ] = useState("");
  const [orden,      setOrden     ] = useState("estado");
  const [filtroEstado, setFiltroEstado] = useState(null);

  const periodo = periodoKey === "custom" ? custom : calcPeriodo(periodoKey);
  const { desde, hasta } = periodo;

  const fetchPeriod = useCallback(async (d, h) => {
    const res = await axios.get(`/api/analisis/asistentas?desde=${d}&hasta=${h}`);
    return res.data;
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const pp = prevPeriodo({ desde, hasta });
      const [curr, prev] = await Promise.all([
        fetchPeriod(desde, hasta),
        fetchPeriod(pp.desde, pp.hasta),
      ]);
      setDatos(curr);
      setDatosPrev(prev);
    } catch (e) {
      setError(e.response?.data?.error || "Error al cargar datos.");
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, fetchPeriod]);

  useEffect(() => { cargar(); }, [cargar]);

  const prevMap = useMemo(() => {
    if (!datosPrev) return {};
    return Object.fromEntries((datosPrev.asistentas || []).map(a => [a.id_asistenta, a]));
  }, [datosPrev]);

  const deltaMap = useMemo(() => {
    const out = {};
    (datos?.asistentas || []).forEach(a => {
      const p   = prevMap[a.id_asistenta];
      const cur = pctTareas(a.tareas);
      const prv = p ? pctTareas(p.tareas) : null;
      out[a.id_asistenta] = { tareas: cur != null && prv != null ? cur - prv : null };
    });
    return out;
  }, [datos, prevMap]);

  const asistentas = useMemo(() => {
    let lista = datos?.asistentas || [];
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      lista = lista.filter(a => a.nombre?.toLowerCase().includes(q));
    }
    if (filtroEstado) lista = lista.filter(a => estadoAsistenta(a) === filtroEstado);
    const sorted = [...lista];
    if (orden === "estado")      sorted.sort((a, b) => ORDEN_ESTADO[estadoAsistenta(a)] - ORDEN_ESTADO[estadoAsistenta(b)]);
    else if (orden === "peor")   sorted.sort((a, b) => (pctTareas(a.tareas) ?? 100) - (pctTareas(b.tareas) ?? 100));
    else if (orden === "nombre") sorted.sort((a, b) => a.nombre.localeCompare(b.nombre));
    else if (orden === "mal")    sorted.sort((a, b) => b.valoracion.mal - a.valoracion.mal);
    else if (orden === "incid")  sorted.sort((a, b) => b.incidencias.reduce((s,i)=>s+i.count,0) - a.incidencias.reduce((s,i)=>s+i.count,0));
    return sorted;
  }, [datos, busqueda, orden, filtroEstado]);

  const estadoCounts = useMemo(() => {
    const c = { critico: 0, atencion: 0, bien: 0, sin_reportes: 0 };
    (datos?.asistentas || []).forEach(a => { c[estadoAsistenta(a)]++; });
    return c;
  }, [datos]);

  const aSeleccionada = useMemo(() => asistentas.find(a => a.id_asistenta === seleccionada), [asistentas, seleccionada]);

  const prevLabel = useMemo(() => {
    const pp = prevPeriodo({ desde, hasta });
    return `${pp.desde} → ${pp.hasta}`;
  }, [desde, hasta]);

  const atencion = useMemo(() => {
    const lista = datos?.asistentas || [];
    let rojas = 0, incid = 0, pend = 0, noVal = 0;
    lista.forEach(a => {
      if (semaforo(pctTareas(a.tareas)) === "rojo") rojas++;
      incid += a.incidencias.reduce((s, i) => s + i.count, 0);
      pend += a.n_pendientes_validacion || 0;
      noVal += a.n_no_validados || 0;
    });
    const items = [];
    if (rojas > 0) items.push({ icon: "🔴", severidad: "rojo", n: rojas, texto: `${rojas} asistenta${rojas !== 1 ? "s" : ""} con tareas por debajo del 60%` });
    if (noVal > 0) items.push({ icon: "❌", severidad: "rojo", n: noVal, texto: `${noVal} día${noVal !== 1 ? "s" : ""} marcado${noVal !== 1 ? "s" : ""} como NO válido${noVal !== 1 ? "s" : ""}` });
    if (incid > 0) items.push({ icon: "⚠️", severidad: "amarillo", n: incid, texto: `${incid} incidencia${incid !== 1 ? "s" : ""} reportada${incid !== 1 ? "s" : ""}` });
    if (pend > 0) items.push({ icon: "🔍", severidad: "amarillo", n: pend, texto: `${pend} día${pend !== 1 ? "s" : ""} pendiente${pend !== 1 ? "s" : ""} de validar` });
    return items;
  }, [datos]);

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Segoe UI', system-ui, sans-serif", paddingBottom: 60 }}>

      {/* HEADER */}
      <div style={{ background: "linear-gradient(135deg,#1e293b,#0f172a)", color: "#fff", padding: "14px 16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "8px 14px" }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Análisis del período</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>🧹 Mis Asistentas</div>
        </div>
        {!loading && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", textAlign: "right" }}>
          {asistentas.length} asistentas
        </div>}
      </div>

      {/* PERÍODO */}
      <div style={{ display: "flex", gap: 6, padding: "10px 14px", overflowX: "auto", background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
        {PERIODOS.map(p => (
          <button key={p.key} onClick={() => setPeriodoKey(p.key)} style={{
            padding: "7px 14px", borderRadius: 20,
            border: periodoKey === p.key ? "1.5px solid #1e293b" : "1.5px solid #e2e8f0",
            background: periodoKey === p.key ? "#1e293b" : "#f8fafc",
            color: periodoKey === p.key ? "#fff" : "#475569",
            fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
          }}>
            {p.label}
          </button>
        ))}
      </div>
      {periodoKey === "custom" && (
        <div style={{ display: "flex", gap: 10, padding: "8px 16px", alignItems: "center", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Desde</label>
          <input type="date" value={custom.desde} max={custom.hasta}
            onChange={e => setCustom(c => ({ ...c, desde: e.target.value }))}
            style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
          <label style={{ fontSize: 13, fontWeight: 600 }}>Hasta</label>
          <input type="date" value={custom.hasta} min={custom.desde} max={todayISO()}
            onChange={e => setCustom(c => ({ ...c, hasta: e.target.value }))}
            style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
        </div>
      )}

      {!loading && datos && (
        <div style={{ background: "#eff6ff", padding: "5px 16px", borderBottom: "1px solid #bfdbfe", fontSize: 11, color: "#3b82f6" }}>
          📊 Comparando con período anterior: <strong>{prevLabel}</strong>
        </div>
      )}

      {!loading && datos && datos.asistentas.length > 0 && <AtencionBanner items={atencion} okTexto="Todas las asistentas al día, sin alertas." />}

      {!loading && datos && datos.asistentas.length > 0 && <SemaforoResumen counts={estadoCounts} filtro={filtroEstado} setFiltro={setFiltroEstado} />}

      {!loading && datos && datos.asistentas.length > 0 && <ResumenPanel asistentas={datos.asistentas} />}

      {!loading && datos && datos.asistentas.length > 0 && (
        <div style={{ display: "flex", gap: 8, padding: "10px 16px", alignItems: "center", flexWrap: "wrap" }}>
          <input type="text" placeholder="🔍 Buscar asistenta…" value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{ flex: 1, minWidth: 160, padding: "8px 12px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13, outline: "none" }} />
          <select value={orden} onChange={e => setOrden(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13, background: "#fff", cursor: "pointer" }}>
            <option value="estado">🚦 Estado</option>
            <option value="peor">Peor primero</option>
            <option value="nombre">Nombre A-Z</option>
            <option value="mal">Más días mal</option>
            <option value="incid">Más incidencias</option>
          </select>
        </div>
      )}

      <div style={{ padding: "0 16px" }}>
        {loading && (
          <div style={{ textAlign: "center", padding: 60, color: "#64748b" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Cargando análisis…</div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>Cargando datos actuales y anteriores</div>
          </div>
        )}
        {error && <div style={{ background: "#fee2e2", color: "#b91c1c", borderRadius: 10, padding: "12px 16px", margin: "12px 0", fontWeight: 600 }}>❌ {error}</div>}
        {!loading && !error && asistentas.length === 0 && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#374151" }}>Sin datos para este período</div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 6 }}>Prueba con otro rango de fechas</div>
          </div>
        )}
        {!loading && asistentas.map(a => (
          <TarjetaAsistenta
            key={a.id_asistenta} a={a}
            prev={prevMap[a.id_asistenta]}
            delta={deltaMap[a.id_asistenta]}
            onClick={() => setSeleccionada(a.id_asistenta)}
          />
        ))}
      </div>

      {seleccionada != null && aSeleccionada && (
        <DetalleModal
          a={aSeleccionada}
          prev={prevMap[seleccionada]}
          delta={deltaMap[seleccionada]}
          onClose={() => setSeleccionada(null)}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ESTILOS COMPARTIDOS
// ═══════════════════════════════════════════════════════════════════════════
const cs = {
  overlay:  { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" },
  modal:    { background: "#fff", borderRadius: 20, width: "100%", maxWidth: 620, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.25)" },
  mHead:    { padding: "14px 16px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, background: "#f8fafc", borderBottom: "1px solid #e2e8f0", flexShrink: 0 },
  btnX:     { background: "#e2e8f0", border: "none", color: "#475569", fontWeight: 800, fontSize: 16, cursor: "pointer", width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 },
  tabBar:   { display: "flex", overflowX: "auto", borderBottom: "2px solid #e2e8f0", flexShrink: 0 },
  tabBtn:   { padding: "10px 14px", border: "none", background: "transparent", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", borderBottom: "2px solid transparent", marginBottom: -2 },
  tabActive:{ color: "#1e293b", borderBottom: "2px solid #1e293b" },
  mBody:    { overflowY: "auto", padding: "14px 16px", flex: 1, display: "flex", flexDirection: "column", gap: 14 },
  sec:      { background: "#f8fafc", borderRadius: 12, padding: "12px 14px", border: "1px solid #e2e8f0" },
  sh:       { margin: "0 0 10px 0", fontSize: 14, fontWeight: 800, color: "#374151" },
};

export default AnalisisAsitentas;
